import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import api from "../../services/api";
import { API_ENDPOINTS } from "../../constants/API_ENDPOINTS";

const validationSchema = Yup.object({
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Please confirm your password"),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const verifyToken = useCallback(async () => {
    if (!token) {
      setError("No reset token provided. Please use the link from your email.");
      setTokenValid(false);
      return;
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.VERIFY_RESET_TOKEN}/${token}`);
      if (response.data.valid) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
        setError(response.data.message || "Invalid or expired reset token");
      }
    } catch (err: any) {
      setTokenValid(false);
      setError("Failed to verify reset token. Please request a new password reset.");
    }
  }, [token]);

  useEffect(() => {
    // Verify token when component mounts
    if (token) {
      verifyToken();
    } else {
      setError("No reset token provided. Please use the link from your email.");
      setTokenValid(false);
    }
  }, [token, verifyToken]);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!token) {
        setError("No reset token provided");
        return;
      }

      setError(null);
      setSuccess(null);
      try {
        const response = await api.post(API_ENDPOINTS.RESET_PASSWORD, {
          token: token,
          new_password: values.password,
        });
        
        setSuccess(response.data.message || "Password has been reset successfully!");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || err.message || "Failed to reset password";
        setError(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (tokenValid === null) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography>Verifying reset token...</Typography>
        </Box>
      </Container>
    );
  }

  if (tokenValid === false) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "Invalid or expired reset token"}
          </Alert>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate("/forgot-password")}
            sx={{ cursor: "pointer" }}
          >
            Request a new password reset
          </Link>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography component="h1" variant="h5">
          Reset Password
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
          Enter your new password below.
        </Typography>
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1, width: "100%" }}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="New Password"
            type={showPassword ? "text" : "password"}
            id="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowConfirmPassword((show) => !show)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={formik.isSubmitting || !!success}
          >
            {formik.isSubmitting ? "Resetting..." : success ? "Password Reset!" : "Reset Password"}
          </Button>
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate("/login")}
              sx={{ cursor: "pointer" }}
            >
              Back to Login
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default ResetPassword;
